import { inject, Injectable } from '@angular/core';
import {
  DataModel,
  RestService,
  RestSettings,
} from '@infodata-group/inf-commons';
import { Action, State, StateContext } from '@ngxs/store';
import { catchError, map, tap } from 'rxjs/operators';
import { ToolsDtoFactory } from '../../engine/tools/tools-dto.factory';
import { CartographyServiceResourceEventEnum } from '../../module/cartography/enum/cartography-service-resource-event.enum';
import { BoCartographyGroupInterface } from '../../module/cartography/interface/back-office/bo-cartography-group.interface';
import { BoCartographyStatusBbxInterface } from '../../module/cartography/interface/back-office/bo-cartography-status-bbx.interface';
import { BoCartographyPositionBbxInterface } from '../../module/cartography/interface/back-office/cartography-position/bo-cartography-position-bbx.interface';
import { BoCartographyResourceBbxInterface } from '../../module/cartography/interface/back-office/cartography-resource/bo-cartography-resource-bbx.interface';
import {
  MapBbxClearAll,
  MapBbxDeleteBbx,
  MapBbxDeleteDataModel,
  MapBbxDeleteGroups,
  MapBbxDeletePositions,
  MapBbxDeleteStatus,
  MapBbxEvent,
  MapBbxLoadBbx,
  MapBbxLoadDataModel,
  MapBbxLoadGroups,
  MapBbxLoadGroupsAndResources,
  MapBbxLoadPositions,
  MapBbxLoadStatus,
} from '../actions/map-bbx.actions';

const REST_SETTINGS: RestSettings = {
  showLoading: false,
};

const DEFAULT_STATE = {
  resources: [],
  resourcesDateLoad: null,
  isResourcesLoading: false,
  groups: [],
  groupsDateLoad: null,
  isGroupsLoading: false,
  positions: [],
  positionsDateLoad: null,
  isPositionsLoading: false,
  status: [],
  statusDateLoad: null,
  isStatusLoading: false,
  dataModel: new DataModel(),
  dataModelDateLoad: null,
  isDataModelLoading: false,
  event: null,
};

export class MapBbxStateModel {
  resources: BoCartographyResourceBbxInterface[];
  resourcesDateLoad: number;
  isResourcesLoading: boolean;
  groups: BoCartographyGroupInterface[];
  groupsDateLoad: number;
  isGroupsLoading: boolean;
  positions: BoCartographyPositionBbxInterface[];
  positionsDateLoad: number;
  isPositionsLoading: boolean;
  status: BoCartographyStatusBbxInterface[];
  statusDateLoad: number;
  isStatusLoading: boolean;
  dataModel: DataModel;
  dataModelDateLoad: number;
  isDataModelLoading: boolean;
  event: CartographyServiceResourceEventEnum;
}
@State<MapBbxStateModel>({
  name: 'mapBbx',
  defaults: DEFAULT_STATE,
})
@Injectable()
export class MapBbxState {
  private restService = inject(RestService);

  @Action(MapBbxLoadGroups)
  loadGroups(state: StateContext<MapBbxStateModel>) {
    state.patchState({
      isGroupsLoading: true,
      event: CartographyServiceResourceEventEnum.loadingGroup,
    });
    return this.restService
      .getAll(ToolsDtoFactory.getCartographyGroupBbxDtoFactory(), REST_SETTINGS)
      .pipe(
        tap((groups) => {
          if (!groups.find((g) => g.id === 'XXX')) {
            groups.push({
              id: 'XXX',
              label: 'Groupe inconnu',
            });
          }
          state.patchState({
            groups: groups,
            groupsDateLoad: new Date().getTime(),
            isGroupsLoading: false,
            event: CartographyServiceResourceEventEnum.loadedGroup,
          });
        }),
        catchError(() => {
          state.patchState({
            isGroupsLoading: false,
          });
          return [];
        }),
      );
  }

  @Action(MapBbxLoadBbx)
  loadResources(state: StateContext<MapBbxStateModel>) {
    state.patchState({
      isResourcesLoading: true,
      event: CartographyServiceResourceEventEnum.loadingResource,
    });
    return this.restService
      .getAll(
        ToolsDtoFactory.getCartographyResourceBbxDtoFactory(),
        REST_SETTINGS,
      )
      .pipe(
        tap((bbx) => {
          state.patchState({
            resources: bbx,
            resourcesDateLoad: new Date().getTime(),
            isResourcesLoading: false,
            event: CartographyServiceResourceEventEnum.loadedResource,
          });
        }),
        catchError(() => {
          state.patchState({
            isResourcesLoading: false,
          });
          return [];
        }),
      );
  }

  @Action(MapBbxDeleteBbx)
  deleteResources(state: StateContext<MapBbxStateModel>) {
    state.patchState({
      resources: [],
      resourcesDateLoad: null,
    });
  }

  @Action(MapBbxDeleteGroups)
  deleteGroups(state: StateContext<MapBbxStateModel>) {
    state.patchState({
      groups: [],
      groupsDateLoad: null,
    });
  }

  @Action(MapBbxLoadPositions)
  loadPositions(state: StateContext<MapBbxStateModel>) {
    state.patchState({
      isPositionsLoading: true,
    });
    return this.restService
      .getAll(
        ToolsDtoFactory.getCartographyPositionBbxDtoFactory(),
        REST_SETTINGS,
      )
      .pipe(
        tap((positions) => {
          state.patchState({
            positions: positions,
            positionsDateLoad: new Date().getTime(),
            isPositionsLoading: false,
          });
        }),
        catchError(() => {
          state.patchState({
            isPositionsLoading: false,
          });
          return [];
        }),
      );
  }

  @Action(MapBbxDeletePositions)
  deletePositions(state: StateContext<MapBbxStateModel>) {
    state.patchState({
      positions: [],
      positionsDateLoad: null,
    });
  }

  @Action(MapBbxLoadStatus)
  loadStatus(state: StateContext<MapBbxStateModel>) {
    state.patchState({
      isStatusLoading: true,
    });
    return this.restService
      .getAll(
        ToolsDtoFactory.getCartographyStatusBbxDtoFactory(),
        REST_SETTINGS,
      )
      .pipe(
        tap((status) => {
          state.patchState({
            status: status,
            statusDateLoad: new Date().getTime(),
            isStatusLoading: false,
          });
        }),
        catchError(() => {
          state.patchState({
            isStatusLoading: false,
          });
          return [];
        }),
      );
  }

  @Action(MapBbxDeleteStatus)
  deleteStatus(state: StateContext<MapBbxStateModel>) {
    state.patchState({
      status: [],
      statusDateLoad: null,
    });
  }

  @Action(MapBbxLoadGroupsAndResources)
  loadGroupsAndResources(state: StateContext<MapBbxStateModel>) {
    state.dispatch(new MapBbxLoadGroups()).subscribe({
      next: () => {
        state.dispatch(new MapBbxLoadBbx());
      },
    });
  }

  @Action(MapBbxLoadDataModel)
  loadDataModelBbx(state: StateContext<MapBbxStateModel>) {
    state.patchState({
      isDataModelLoading: true,
      event: CartographyServiceResourceEventEnum.loadingModel,
    });
    return this.restService
      .options(
        `${ToolsDtoFactory.getCartographyResourceBbxDtoFactory().getApi()}?PRF=INFOBULLE`,
        REST_SETTINGS,
      )
      .pipe(
        map((model) => {
          const dateProperty = model.find('date');
          if (dateProperty) {
            dateProperty.id = 'dateText';
          }
          return model;
        }),
        tap((dataModel) => {
          state.patchState({
            dataModel: dataModel,
            dataModelDateLoad: new Date().getTime(),
            isDataModelLoading: false,
            event: CartographyServiceResourceEventEnum.loadedModel,
          });
        }),
      );
  }

  @Action(MapBbxDeleteDataModel)
  deleteDataModelBbx(state: StateContext<MapBbxStateModel>) {
    state.patchState({
      dataModel: new DataModel(),
      dataModelDateLoad: null,
    });
  }

  @Action(MapBbxClearAll)
  clearAll(state: StateContext<MapBbxStateModel>) {
    state.patchState(DEFAULT_STATE);
  }

  @Action(MapBbxEvent)
  event(state: StateContext<MapBbxStateModel>, action: MapBbxEvent) {
    state.patchState({
      event: action.payload,
    });
  }
}
